import { ForwardedRef, forwardRef, useState } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, PlanRecord, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { DatePicker, DatePickerProps } from "@material-ui/pickers"
import { css, cx } from "@emotion/css"

import { Bulky } from "./Bulky"
import Button from "ui/buttons/Button"
import { CellError } from "./CellError"
import { FieldsKey } from "../components/AutoCompute/types"
import { Info } from "./Info"
import { get } from "lodash"
import { getMaxDateSameMonth, isSemanticReadonly } from "../utils"
import { set } from "lodash/fp"
import { startOfDay } from "date-fns"
import { useAuthenticated } from "providers/A12n"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useDefaultFieldIds } from "../useDefaultFieldIds"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { useParams } from "react-router-dom"
import { queryCache } from "react-query"
import { MediaPlan } from "../../../../../models/mediaPlan"
import { Paginated } from "../../../../../api/types"
import { MediaType } from "../../../Admin/GlobalMediaTypes/types"

export function DateCell(props: CellProps) {
  const { rowId: id, dataKey, columnId, disabled, primaryKey, semantic } = props
  const { meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const defaultFieldIds = useDefaultFieldIds(id)
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const { id: planId } = useParams<{ id: string }>()
  const writeTo = tableSource.state

  const action = readFrom[id]?.action
  const type = readFrom[id]?.type as PlanRecordType
  const packaged = readFrom[id]?.packaged
  const updatable = !action || action === "UPDATE_ORDER"
  const error = get(readError, `${id}.${columnId}`)
  let value = get(readFrom, `${id}.${dataKey}`, null) as string | null

  const planningMediaFields = readFrom[id]?.planningMediaFields ?? {}

  const { handleFocus, hasChanged } = useAutocomputeValidation(
    value || "",
    true,
  )

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const handleChange = (val: Date | null) => {
    if (!!val) {
      globalUndoState.push([writeTo[id] as ChangedPlanRecord])
      writeTo[id] = set(
        dataKey,
        zonedTimeToUtc(val, timeZone).toISOString(),
        writeTo[id],
      )
      tableSource.clearError(id)
      updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
      hasChanged(zonedTimeToUtc(val, timeZone).toISOString()) &&
        autoCompute?.compute(writeTo[id] as ChangedPlanRecord)

      if (
        semantic === "start" &&
        mediaIdLiLimited(readFrom[id]?.mediaId, readFrom[id]?.ancestorMediaId)
      ) {
        validateEndDate(val)
      }
    }
  }

  const validateEndDate = (startDate: number | Date) => {
    const endDateKey = defaultFieldIds?.end
    if (!!endDateKey) {
      const selectedEndDate = new Date(
        (planningMediaFields[endDateKey] ?? "").toString(),
      )
      const selectedStartDate = new Date(startDate)

      if (
        selectedEndDate.getMonth() !== selectedStartDate.getMonth() ||
        selectedEndDate < startDate
      ) {
        const endKey = `planningMediaFields.${endDateKey}`
        writeTo[id] = set(endKey, null, writeTo[id])
        autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
      }
    }
  }

  const { getInputProps } = useFocusManagement()

  const mediaFields = readFrom[id]?.[
    primaryKey as keyof PlanRecord
  ] as PlanRecord["planningMediaFields"]

  const readOnly = isSemanticReadonly(
    type,
    primaryKey as FieldsKey,
    semantic,
    packaged,
    defaultFieldIds,
    mediaFields,
  )

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  const mediaIdLiLimited = (
    mediaId: string | undefined,
    parentMediaId: string | undefined,
  ) => {
    const ps = new URLSearchParams([
      ["configScope", "MEDIA_PLAN"],
      ["configOwnerId", planId],
    ])
    const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
      "media-types",
      ps.toString(),
    ])

    return (
      mediaTypes?.data?.find(
        (item) => item?.id === mediaId || item?.id === parentMediaId,
      )?.liDateRangeLimited ?? false
    )
  }

  const getDates = () => {
    let additionalProps = {}

    if (
      mediaIdLiLimited(readFrom[id]?.mediaId, readFrom[id]?.ancestorMediaId) &&
      readFrom[id]?.planningMediaFields
    ) {
      const plan = queryCache.getQueryData<MediaPlan>(["plan", planId, null])

      if (semantic === "start") {
        additionalProps = {
          minDate: plan?.start ?? "",
          maxDate: plan?.end ?? "",
        }
      }

      if (semantic === "end" && defaultFieldIds?.end) {
        const startDate =
          planningMediaFields[defaultFieldIds?.start] ?? plan?.start

        const endDate = getMaxDateSameMonth(
          utcToZonedTime(new Date(startDate?.toString() ?? ""), timeZone),
          utcToZonedTime(new Date(plan?.end ?? ""), timeZone),
        )

        additionalProps = {
          minDate: startDate,
          maxDate: endDate,
        }
      }
    }
    return additionalProps
  }

  return (
    <div className={styles.wrapper}>
      <Bulky {...props}>
        <ThisDateInput
          name={dataKey}
          value={value}
          onChange={handleChange}
          disabled={disabled || readOnly || isDisabled}
          {...getInputProps()}
          {...getDates()}
          onFocus={handleFocus}
        />
      </Bulky>
      <Info {...props} />
      {!!error ? <CellError error={error} /> : null}
    </div>
  )
}

type ThisDateInputProps = {
  name: string
  onChange: (date: Date | null, name: string) => void
  value: string | Date | undefined | null
} & Omit<DatePickerProps, "name" | "onChange">

const ThisDateInput = forwardRef(
  (props: ThisDateInputProps, ref: ForwardedRef<any>) => {
    const { name, value, onChange, InputProps, error, disabled, ...rest } =
      props
    const [open, setOpen] = useState<boolean>(false)

    const handleChange = (d: Date | null) => {
      if (d) {
        /*
        date range inputs return the date at start of day
        this input returns the current date
        when we convert dates to ISOString
        these two may have one calendar day difference
        resulting in validation errors

        so we make the change here to return the date at start of day too
        as we don't care about precision in hours or down anywhere in the app
      */
        const _d = startOfDay(d)
        onChange(_d, name)
      } else {
        onChange(null, name)
      }
    }

    const handleOpen = () => {
      setOpen(!open)
    }

    const empty = value == null

    // min date should be current from midnight
    // const minDate = new Date()
    // minDate.setHours(0, 0, 0, 0)

    return (
      <DatePicker
        {...rest}
        ref={ref}
        disabled={disabled}
        format="P"
        variant="dialog"
        inputVariant="standard"
        disableToolbar={true}
        allowKeyboardControl={true}
        fullWidth={true}
        invalidDateMessage={null}
        name={name}
        emptyLabel={disabled ? "" : "choose date"}
        okLabel={
          <Button color="primary" variant="contained">
            {"Apply"}
          </Button>
        }
        cancelLabel={
          <Button color="primary" variant="outlined">
            {"Cancel"}
          </Button>
        }
        value={value}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleOpen}
        // minDate={minDate}
        minDateMessage={""}
        maxDateMessage={""}
        InputProps={{
          disableUnderline: true,
          classes: {
            root: styles.inputContainer,
            input: cx(styles.input, { [styles.empty]: empty }),
          },
        }}
      />
    )
  },
)

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
  `,
  inputContainer: css`
    min-height: var(--spacing-xl);
    color: inherit;
  `,
  input: css`
    padding: 0;
    text-align: right;
    font-size: 13px;
    font-weight: inherit;
  `,
  empty: css`
    color: var(--grey, gray);
  `,
}
